import React from 'react';
import { Route, Switch } from 'react-router-dom';
import styled from 'styled-components';
import Header from '@this/shared/header/header';
import Footer from '@this/shared/footer/footer';
import type UserJson from '@this/domain/user/user_json';
import User from '@this/domain/user/user';
import { Fetcher, StrictFetcher } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';

interface Props {
  serviceId?: number;
}

interface State {
  user: User | null;
}

function getStyles() {
  return {
    formBlock: {
      width: '100%'
    } as React.CSSProperties,
    text: {
      marginBottom: '20px'
    } as React.CSSProperties,
    subtitle: {
      fontWeight: 'bold',
      fontSize: '22px',
      marginBottom: '20px'
    } as React.CSSProperties,
    sectionNote: {
      fontSize: '14px',
      color: '#666'
    } as React.CSSProperties,
    form: {
      width: '100%'
    } as React.CSSProperties,
    formBody: {
      background: '#fff'
    } as React.CSSProperties,
    field: {
      background: '#fff',
      borderBottom: '1px solid #eee',
      padding: '10px'
    } as React.CSSProperties,
    inputArea: {} as React.CSSProperties,
    label: {
      color: '#0080cb',
      fontWeight: 'bold',
      fontSize: '12px',
      display: 'block',
      marginBottom: '4px'
    } as React.CSSProperties,
    required: {
      color: '#dc3545',
      fontSize: '11px',
      marginLeft: '6px'
    } as React.CSSProperties,
    input: {
      width: '100%',
      boxSizing: 'border-box',
      minHeight: 'auto',
      padding: '0.5em',
      border: '1px solid #eee',
      borderRadius: '3px',
      boxShadow: 'inset 0 1px 3px rgb(0 0 0 / 6%)',
      background: '#fff'
    } as React.CSSProperties,
    error: {
      color: '#dc3545',
      margin: '20px 0 0',
      whiteSpace: 'pre-wrap'
    } as React.CSSProperties,
    actionButtonRow: {
      display: 'flex',
      justifyContent: 'center',
      marginTop: '20px'
    } as React.CSSProperties,
    submitButton: {
      display: 'block',
      background: 'white',
      border: '2px solid #0080cb',
      color: '#0080cb',
      fontSize: '20px',
      padding: '18px',
      borderRadius: '30px',
      margin: '20px auto 0',
      width: '200px',
      minHeight: 'unset',
      boxShadow: 'none',
      cursor: 'pointer'
    } as React.CSSProperties,
    submitButtonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed'
    } as React.CSSProperties,
    finishWrap: {
      marginBottom: '20px'
    } as React.CSSProperties,
    finish: {
      padding: '0'
    } as React.CSSProperties,
    finishLabel: {
      fontWeight: 'bold',
      fontSize: '22px',
      marginBottom: '20px'
    } as React.CSSProperties,
    finishText: {
      lineHeight: 1.7
    } as React.CSSProperties,
    title: {
      margin: '0 0 16px',
      fontSize: '24px'
    } as React.CSSProperties,
    lead: {
      margin: '0 0 12px'
    } as React.CSSProperties
  } as const;
}

class MiceInquiries extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      user: null
    };
  }

  componentDidMount() {
    StrictFetcher.get<UserJson>('/users.json', 'users/index.json').then(
      response => {
        const user = new User(response);
        this.setState({ user });
      },
      () => {
        this.setState({ user: null });
      }
    );
  }

  render() {
    try {
      const { serviceId } = this.props;
      const { user } = this.state;
      const styles = getStyles();

      return (
        <div className="mice-inquiries">
          <PcOnly>
            <div className="mice-inquiries-pc">
              <Header user={user} />
              <MiceInquiriesContentWrapper>
                <Switch>
                  <Route exact path="/mice_inquiries/new" render={() => <MiceInquiryForm styles={styles} />} />
                </Switch>
              </MiceInquiriesContentWrapper>
              <Footer signedIn={!!user} serviceId={serviceId} />
            </div>
          </PcOnly>
        </div>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

type MiceInquiryFormProps = {
  styles: ReturnType<typeof getStyles>;
};

const PcOnly = styled.div`
  min-height: 100vh;

  > :first-child {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin: 0 auto;
  }

  header {
    background: #292929;
    border-bottom: 1px solid #af985e;
  }

  .header-in {
    max-width: 100%;
    background: none;
    border-bottom: none;
    padding: 0 20px;
  }

  .logo {
    padding-left: 0;
  }

  .trips-button {
    margin-right: 0;
  }

  .in-advance-button {
    margin-right: 10px;
  }

  .content-wrapper {
    flex-grow: 9999;
    display: flex;
    flex-direction: column;
    min-height: 0%;
    width: 100%;
    background: #eee;
  }

  .content-body {
    max-width: 1030px;
    padding: 20px;
    background: none;
  }

  .info-content {
    position: relative;
    background: white;

    &__title {
      &__link {
        position: absolute;
        top: 10px;
        right: 10px;
        margin: 0;
      }
    }
  }
`;

const MiceInquiriesContentWrapper = styled.div`
  background-color: ${props => props.theme.grayBgColor};
  padding: 20px;
  flex-grow: 9999;
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
`;

type MiceInquiryFormState = {
  departure: string;
  destination: string;
  departureOn: string;
  nights: string;
  transportation: string;
  budgetCapPerPerson: string;
  budgetCapTotal: string;
  loading: boolean;
  errors: string[];
  succeeded: boolean;
};

const MiceInquiryForm = ({ styles }: MiceInquiryFormProps) => {
  const [state, setState] = React.useState<MiceInquiryFormState>({
    departure: '',
    destination: '',
    departureOn: '',
    nights: '',
    transportation: '',
    budgetCapPerPerson: '',
    budgetCapTotal: '',
    loading: false,
    errors: [],
    succeeded: false
  });

  const handleChange = (
    key:
      | 'departure'
      | 'destination'
      | 'departureOn'
      | 'nights'
      | 'transportation'
      | 'budgetCapPerPerson'
      | 'budgetCapTotal',
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.currentTarget;
    setState(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!state.departure || !state.destination || !state.departureOn || !state.transportation) {
      setState(prev => ({
        ...prev,
        errors: ['必須項目を入力してください。']
      }));
      return;
    }
    setState(prev => ({ ...prev, loading: true, errors: [] }));

    try {
      await Fetcher.post('/mice_inquiries', {
        departure: state.departure,
        destination: state.destination,
        departure_on: state.departureOn,
        nights: state.nights || null,
        transportation: state.transportation,
        budget_cap_per_person: state.budgetCapPerPerson || null,
        budget_cap_total: state.budgetCapTotal || null
      });
      setState(prev => ({ ...prev, loading: false, succeeded: true, errors: [] }));
    } catch (error) {
      const errors = (error as any)?.responseJSON?.errors || [
        '通信エラーが発生しました。しばらく時間をおいて再度お試しください。'
      ];
      setState(prev => ({ ...prev, loading: false, errors }));
    }
  };

  if (state.succeeded) {
    return (
      <div style={styles.formBlock}>
        <div style={styles.finish}>
          <div style={styles.finishLabel}>送信が完了しました</div>
          <div style={styles.finishText}>
            お問い合わせありがとうございます。
            {'\n'}
            内容を確認のうえ担当者よりご連絡します。
            {'\n'}
            しばらく経っても連絡がない場合は mice@aitravel.jp までご連絡ください。
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.formBlock}>
      <div style={styles.text}>
        <div style={styles.subtitle}>団体依頼フォーム</div>
        <p>団体旅行のご依頼はこちらからお送りください。</p>
      </div>
      <form style={styles.form} onSubmit={handleSubmit}>
        <div style={styles.formBody}>
          <div style={styles.field}>
            <label htmlFor="departure" style={styles.label}>
              出発地
              <span style={styles.required}>必須</span>
            </label>
            <div style={styles.inputArea}>
              <input
                id="departure"
                name="departure"
                type="text"
                style={styles.input}
                value={state.departure}
                onChange={e => handleChange('departure', e)}
                required
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="destination" style={styles.label}>
              目的地
              <span style={styles.required}>必須</span>
            </label>
            <div style={styles.inputArea}>
              <input
                id="destination"
                name="destination"
                type="text"
                style={styles.input}
                value={state.destination}
                onChange={e => handleChange('destination', e)}
                required
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="departure_on" style={styles.label}>
              出発日
              <span style={styles.required}>必須</span>
            </label>
            <div style={styles.inputArea}>
              <input
                id="departure_on"
                name="departure_on"
                type="date"
                style={styles.input}
                value={state.departureOn}
                onChange={e => handleChange('departureOn', e)}
                required
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="nights" style={styles.label}>
              泊数
            </label>
            <div style={styles.inputArea}>
              <input
                id="nights"
                name="nights"
                type="number"
                min="0"
                style={styles.input}
                value={state.nights}
                onChange={e => handleChange('nights', e)}
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="transportation" style={styles.label}>
              交通手段
              <span style={styles.required}>必須</span>
            </label>
            <div style={styles.inputArea}>
              <input
                id="transportation"
                name="transportation"
                type="text"
                style={styles.input}
                value={state.transportation}
                onChange={e => handleChange('transportation', e)}
                required
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="budget_cap_per_person" style={styles.label}>
              予算上限（一人あたり）
            </label>
            <div style={styles.inputArea}>
              <input
                id="budget_cap_per_person"
                name="budget_cap_per_person"
                type="number"
                min="0"
                style={styles.input}
                value={state.budgetCapPerPerson}
                onChange={e => handleChange('budgetCapPerPerson', e)}
              />
            </div>
          </div>
          <div style={styles.field}>
            <label htmlFor="budget_cap_total" style={styles.label}>
              予算上限（総額）
            </label>
            <div style={styles.inputArea}>
              <input
                id="budget_cap_total"
                name="budget_cap_total"
                type="number"
                min="0"
                style={styles.input}
                value={state.budgetCapTotal}
                onChange={e => handleChange('budgetCapTotal', e)}
              />
            </div>
          </div>
        </div>
        {state.errors.length > 0 && <div style={styles.error}>{state.errors.join('\n')}</div>}
        <div style={styles.actionButtonRow}>
          <button
            type="submit"
            style={{ ...styles.submitButton, ...(state.loading ? styles.submitButtonDisabled : {}) }}
            disabled={state.loading}
          >
            {state.loading ? '送信中' : '送信'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MiceInquiries;
